/**
 * CZNA-553 — turning the two date pickers of the approval printout into the instants the
 * API takes.
 *
 * The backend deliberately has no timezone of its own: `GET /proposals/pending-report`
 * takes two instants, because the day boundary that matters belongs to the cataloguer
 * sitting in front of the screen, not to the server. This module is where that boundary is
 * computed, and it is the one piece of the feature where an off-by-one is invisible on the
 * paper: a `to` bound set to midnight instead of the end of the day silently drops every
 * proposal handed in on the last day of the period — which, for a meeting held to discuss
 * that period, is the likeliest day of all.
 */
/** How far back the period reaches by default. */
export var DEFAULT_PERIOD_DAYS = 30;
var pad = function (n) { return String(n).padStart(2, '0'); };
/**
 * `YYYY-MM-DD` in LOCAL time.
 *
 * Not `toISOString().slice(0, 10)`: that converts to UTC first, so for any timezone east
 * of Greenwich the first hours of a local day render as the previous date — the picker
 * would open on yesterday for the whole Czech morning.
 */
export var toDateInputValue = function (date) {
    return "".concat(date.getFullYear(), "-").concat(pad(date.getMonth() + 1), "-").concat(pad(date.getDate()));
};
/**
 * The period the screen opens on: the last {@link DEFAULT_PERIOD_DAYS} days, ending today.
 *
 * A default was chosen over an empty form because the screen has one purpose — print the
 * sheet before the meeting — and an empty form makes the first click a data-entry task. It
 * is a starting point, not a claim about NK's meeting cadence, which is not ours to know;
 * both pickers stay editable.
 */
export var defaultReportPeriod = function (today) {
    if (today === void 0) { today = new Date(); }
    var from = new Date(today.getTime());
    from.setDate(from.getDate() - DEFAULT_PERIOD_DAYS);
    return { from: toDateInputValue(from), to: toDateInputValue(today) };
};
/** Splits `YYYY-MM-DD` into its three numbers, or null when it is not that shape. */
var parseDateInput = function (value) {
    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match)
        return null;
    var _a = [Number(match[1]), Number(match[2]), Number(match[3])], year = _a[0], month = _a[1], day = _a[2];
    var probe = new Date(year, month - 1, day);
    // Rejects 2026-02-30 and friends, which the Date constructor rolls over silently.
    if (probe.getFullYear() !== year || probe.getMonth() !== month - 1
        || probe.getDate() !== day) {
        return null;
    }
    return [year, month, day];
};
/**
 * The two dates as instants: local start of the `from` day, local END of the `to` day.
 *
 * Both bounds are inclusive on the server, so the upper one must be 23:59:59.999 local —
 * see the module comment. Returns null when either date is missing or malformed, or when
 * the period is inverted; the caller renders that as a message rather than sending a
 * request the server will refuse with a 400.
 */
export var toPeriodBounds = function (period) {
    var from = parseDateInput(period.from);
    var to = parseDateInput(period.to);
    if (!from || !to)
        return null;
    var start = new Date(from[0], from[1] - 1, from[2], 0, 0, 0, 0);
    var end = new Date(to[0], to[1] - 1, to[2], 23, 59, 59, 999);
    if (start.getTime() > end.getTime())
        return null;
    return { from: start.toISOString(), to: end.toISOString() };
};
